import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Monitor, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/components/email/EmParts";
import EmailBuilder, { exportDesign, showPreview } from "@/components/email/EmailBuilder";
import BuilderSidebar from "@/components/email/BuilderSidebar";
import { starterDesign } from "@/lib/emailDesign";

const KEYS = ["name", "subject", "preview_text", "from_name", "from_email", "reply_to", "audience", "utm_enabled", "utm_campaign", "template_id"];

export default function EmCampaignDesign() {
  const { id } = useParams();
  const nav = useNavigate();
  const editor = useRef(null);
  const [c, setC] = useState(null);
  const [brand, setBrand] = useState({});
  const [design, setDesign] = useState(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([api.get(`/email-marketing/campaigns/${id}`), api.get("/email-marketing/brand")]).then(async ([cr, br]) => {
      setBrand(br.data); setC(cr.data);
      if (cr.data.design) return setDesign(cr.data.design);
      if (cr.data.template_id) { const t = await api.get(`/email-marketing/templates/${cr.data.template_id}`).catch(() => null); if (t?.data?.design) return setDesign(t.data.design); }
      setDesign(starterDesign(br.data));
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!c || design === undefined) return null;
  const save = async () => {
    setBusy(true);
    try { const d = await exportDesign(editor.current); await api.put(`/email-marketing/campaigns/${id}`, { ...Object.fromEntries(KEYS.map((k) => [k, c[k] ?? (k === "utm_enabled" ? true : k === "template_id" ? null : "")])), design: d.design, html: d.html }); toast.success("Campaign design saved"); nav(`/email-marketing/campaigns/${id}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="em-campaign-design">
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/email-marketing/campaigns/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="em-design-back"><ArrowLeft className="w-4 h-4" />{c.name}</Link>
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="outline" onClick={() => showPreview(editor.current, "desktop")} data-testid="em-design-preview-desktop"><Monitor className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => showPreview(editor.current, "mobile")} data-testid="em-design-preview-mobile"><Smartphone className="w-4 h-4" /></Button>
          <Button size="sm" className="gap-1" onClick={save} disabled={busy || !["draft", "scheduled"].includes(c.status)} data-testid="em-design-save"><Save className="w-4 h-4" />Save to campaign</Button>
        </div>
      </div>
      <div className="grid xl:grid-cols-[1fr_280px] gap-3"><EmailBuilder design={design} editorRef={editor} /><BuilderSidebar brand={brand} /></div>
    </div>
  );
}
